import { effect, inject, Injectable, signal } from "@angular/core";
import { ModelStoreAbstraction } from "@bitwarden/web-vault/app/services/store/model.store.abstraction";
import { StoreModelNames } from "@bitwarden/web-vault/app/services/dali/type/dali.type";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import {
  PartialTransactionDeleteError,
  TransactionStoreCollection,
} from "@bitwarden/web-vault/app/services/store/transaction/transaction.store.collection";
import { StoreModelCollection } from "@bitwarden/web-vault/app/services/store/model.collection.abstraction";
import { ReferenceDataStoreCollection } from "@bitwarden/web-vault/app/services/store/transaction/reference-data.store.collection";
import { toObservable } from "@angular/core/rxjs-interop";
import {
  ReferenceDataView,
  toReferenceDataView,
} from "@bitwarden/web-vault/app/models/view/reference-data/reference-data.view";
import { IModelView } from "@bitwarden/web-vault/app/models/view/model.view";
import { DataPluginStoreService } from "@bitwarden/web-vault/app/services/store/data-plugin/data-plugin.store.service";
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  distinctUntilKeyChanged,
  filter,
  Observable,
  skipUntil,
  Subscription,
  tap,
} from "rxjs";
import { format } from "date-fns";
import { TransactionView } from "@bitwarden/web-vault/app/models/view/transaction/transaction.view";
import { SymbolView } from "@bitwarden/web-vault/app/models/view/symbol/symbol.view";
import { ForexRateView } from "@bitwarden/web-vault/app/models/view/reference-data/forex-rate.view";
import { EnrichTransactionGeneratedCollection } from "@bitwarden/web-vault/app/services/store/transaction/enrich-transaction.generated.collection";
import { SymbolsGeneratedCollection } from "@bitwarden/web-vault/app/services/store/transaction/symbols.generated.collection";
import { SymbolsStoreCollection } from "@bitwarden/web-vault/app/services/store/transaction/symbols.store.collection";
import { AccountView } from "@bitwarden/web-vault/app/models/view/account/account.view";
import { SearchData } from "@bitwarden/web-vault/app/services/store/search.abstraction";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import { BalanceAlignmentTransactionsGeneratedCollection } from "@bitwarden/web-vault/app/services/store/calculation-base/generated-transactions/balance-alignment-transactions.generated.collection";
import { GeneratedTransactionParams } from "@bitwarden/web-vault/app/services/store/generated.collection.abstraction";
import { WebWorkerQueue } from "@bitwarden/web-vault/app/services/web-worker/WebWorkerQueue";
import { sortTransactionViews } from "@bitwarden/web-vault/app/shared/utils/helper.transactionView/sort";
import { RevaluationTransactionsGeneratedCollection } from "@bitwarden/web-vault/app/services/store/calculation-base/generated-transactions/revaluation-transactions.generated.collection";
import { AppStateStoreService } from "@bitwarden/web-vault/app/services/store/app-state/app-state.store.service";
import { AlignedTransactionGeneratedCollection } from "@bitwarden/web-vault/app/services/store/transaction/aligned-transaction.generated.collection";
import { BalanceByAccountsAndSymbolsCalculatedCollection } from "@bitwarden/web-vault/app/services/store/transaction/balances/balance-by-accounts-and-symbol.calculated.collection";
import { CalculatedBalanceParams } from "@bitwarden/web-vault/app/services/store/calculated-balance.collection.abstraction";
import { isCurrency } from "@bitwarden/web-vault/app/models/view/symbol/currency-utility";
import { BALANCE_DEFINITIONS } from "@bitwarden/web-vault/app/models/enum/transactionType";
import { NotificationEnum } from "@bitwarden/web-vault/app/models/enum/notification.enum";
import { ConfirmationDialogService } from "@bitwarden/web-vault/app/services/confirmation/confirmation.service";
import { deletingItem } from "@bitwarden/web-vault/app/components/tables/deleteItem";
import { GlossSyncService } from "@bitwarden/web-vault/app/services/syncing/gloss.sync.service";

interface TransactionStorePage {
  isDirtyForexRate: boolean;
  isDirtySymbols: boolean;
  isDirtyTransactions: boolean;
}

const StateIsDirty: TransactionStorePage = {
  isDirtyForexRate: true,
  isDirtySymbols: true,
  isDirtyTransactions: true,
};

const NoDirty: TransactionStorePage = {
  isDirtyForexRate: false,
  isDirtySymbols: false,
  isDirtyTransactions: false,
};

type SymbolsStageResult = TransactionStorePage | "fullRerun";

interface TransactionStoreState {
  status:
    | "Initialise"
    | "Idle"
    | "Rerun"
    | "PullingForexRates"
    | "UpdatingSymbols"
    | "EnrichingTransactions"
    | "AligningTransactions"
    | "RevaluatingTransactions"
    | "CalculatingBalances";
}

const API_DATE_FORMAT = "yyyy-MM-dd";

@Injectable({
  providedIn: "root",
})
export class TransactionStoreService extends ModelStoreAbstraction {
  protected log: LogService = inject(LogService);
  protected appState: AppStateStoreService = inject(AppStateStoreService);
  protected dataPluginStore = inject(DataPluginStoreService);
  protected userStoreService = inject(UserStoreService);
  protected confirmationDialogService = inject(ConfirmationDialogService);
  private glossSyncService = inject(GlossSyncService);

  /** Collection **/
  readonly referenceData: ReferenceDataStoreCollection = new ReferenceDataStoreCollection();
  readonly transaction: TransactionStoreCollection = new TransactionStoreCollection();
  /**
   * @deprecated Use the new {@link TransactionStoreService#symbols} instead.
   */
  readonly generatedSymbols: SymbolsGeneratedCollection = new SymbolsGeneratedCollection();
  readonly symbols: SymbolsStoreCollection = new SymbolsStoreCollection();
  readonly enrichTransaction: EnrichTransactionGeneratedCollection =
    new EnrichTransactionGeneratedCollection();
  readonly alignedTransactionGeneratedCollection: AlignedTransactionGeneratedCollection =
    new AlignedTransactionGeneratedCollection();

  /** Generated Transaction Collection **/
  balanceAlignmentTransactions = new BalanceAlignmentTransactionsGeneratedCollection();
  revaluationTransactions = new RevaluationTransactionsGeneratedCollection();

  /** Calculated Balances **/
  balanceByAccountSymbols = new BalanceByAccountsAndSymbolsCalculatedCollection();
  private _references = signal<ForexRateView[]>([]);
  readonly references = this._references.asReadonly();

  /** Observables that this store has dependencies on **/
  private preference$ = toObservable(this.userStoreService.preferences.preferenceView);
  private firstTransactionDate$: Observable<Date> = toObservable(
    this.transaction.firstTransactionDate,
  );
  private referenceData$: Observable<ReferenceDataView[]> = toObservable(
    this.referenceData.referenceDataViews,
  );
  private transaction$: Observable<TransactionView[]> = toObservable(
    this.transaction.transactionViews,
  );
  private accounts$ = this.userStoreService.enrichAccounts.collection$;
  private creditReferences$ = toObservable(this.references);

  /** Store internal loading state **/
  private _storeState$: BehaviorSubject<TransactionStoreState> = new BehaviorSubject({
    status: "Initialise",
  });

  /** Merged dirty flags for a single trailing run when refresh is busy or blocked by sync. */
  private pendingDirty: TransactionStorePage | null = null;
  private refreshRunning = false;
  /** Bumped by clearStore(). clearStore() can't cancel an in-flight runRefreshCycle, so the cycle
   * captures this at start and checks it after every await: a cycle from before the clear (e.g.
   * the previous user's, on logout) must not write into the cleared store, and must not reset
   * refreshRunning / pendingDirty / status, which by then may belong to the next session's cycle. */
  private storeEpoch = 0;

  private readonly flushRefreshWhenSyncUnblocks = effect(() => {
    if (this.glossSyncService.isRefreshBlocked()) {
      return;
    }
    const pending = this.pendingDirty;
    if (!pending) {
      return;
    }
    this.pendingDirty = null;
    this.scheduleRefresh(pending);
  });

  /** Internal Variables **/
  webWorkerQueue = WebWorkerQueue.getInstance();
  private subscriptions: Subscription[] = [];

  clearStore(): void {
    this.subscriptions.forEach((s) => {
      s.unsubscribe();
    });
    this.subscriptions = [];

    this.generatedSymbols.clear();
    this.symbols.clear();
    this.transaction.clear();
    this.referenceData.clear();
    this._references.set([]);
    this.enrichTransaction.clear();
    // Read by the past/today calculation stores (transaction.subscriber.ts), the monthly summary
    // and calculateBalances(); left populated, a recompute before the next user's data loads
    // would read the previous user's transactions.
    this.alignedTransactionGeneratedCollection.clear();
    // Without this, balanceByAccountSymbols keeps serving pre-clear balances for accounts/symbols
    // that no longer exist post-clear (see ScenarioStoreService.clearStore(), which already does
    // this for its own copy of this collection).
    this.balanceByAccountSymbols.clear();
    // Holds the previous session's balance-alignment output; clearing it also makes an alignment
    // still awaiting the worker drop its result (see its clear()).
    this.balanceAlignmentTransactions.clear();
    this.storeEpoch++;
    this.pendingDirty = null;
    this.refreshRunning = false;
    this.log.info("TransactionStoreService cleared");
  }

  async saveGlossCreditsReferences(references: ForexRateView[]): Promise<void> {
    this._references.set(references);
    await this.ensureCreditFiatForexRates(references);
  }

  /**
   * Credit references only anchor the credit symbol (e.g. CrS) to its fiat currency (AUD).
   * For a non-AUD base user the rate graph still needs an AUD-to-base edge to chain
   * CrS -> AUD -> base, otherwise the credit value normalizes to 0. Credit transactions
   * carry the fiat only in `currency`, which is not collected as an active symbol, so the
   * regular forex stage never fetches it. Pull it explicitly here.
   */
  private async ensureCreditFiatForexRates(references: ForexRateView[]): Promise<void> {
    const coreCurrency = this.userStoreService.preferences.preferenceView()?.coreCurrency;
    if (!coreCurrency) {
      return;
    }

    const fiatCurrencies = new Set<string>();
    for (const ref of references ?? []) {
      if (ref?.base && isCurrency(ref.base)) {
        fiatCurrencies.add(ref.base);
      }
      for (const symbol of Object.keys(ref?.symbols ?? {})) {
        if (isCurrency(symbol)) {
          fiatCurrencies.add(symbol);
        }
      }
    }

    const currencies = [...fiatCurrencies].filter((currency) => currency !== coreCurrency);
    if (currencies.length === 0) {
      return;
    }

    const firstTransactionDate = this.transaction.firstTransactionDate();

    // no transactions loaded yet - nothing to compute a fetch range from
    if (!firstTransactionDate) {
      return;
    }

    const range = this.generatedSymbols.recalcDateRange(currencies, [
      firstTransactionDate,
      new Date(),
    ]);

    // null means every currency's requested range is already fully covered by existing history
    if (!range) {
      return;
    }

    await this.pullNewForexRates(coreCurrency, range.currencies, range.startDate, range.endDate);
  }

  initialize(): void {
    this.log.info("Initializing TransactionStoreService");
    /** Load Reference Data and Transaction Data**/
    this.referenceData.triggerChanges();
    this.transaction.triggerChanges();
    this.symbols.triggerChanges();
    this.initialiseCollectionListener();
  }

  private initialiseCollectionListener(): void {
    this.subscribeToDataChange();
  }

  /**
   * Subscription for external data change in other stores
   **/
  private subscribeToDataChange() {
    // when the base currency is updated
    const baseCurrencyChange$ = this.preference$.pipe(
      filter((x) => {
        return !!x || !!x?.coreCurrency;
      }),
      distinctUntilKeyChanged("coreCurrency"),
      // fetch-date coverage recorded so far is scoped to the previous core currency and
      // no longer valid - clearing it lets recalcDateRange naturally fall back to a full refetch
      tap(() => this.generatedSymbols.clearFetchDateRange()),
    );

    // when the first transaction date is updated
    const firstTransactionDateChange$ = this.firstTransactionDate$.pipe(
      filter((x) => !!x),
      distinctUntilChanged((prev, curr) => {
        return prev.toISOString() === curr.toISOString();
      }),
    );

    // no need to listen to the sybmbols collection changes,
    // as it is generated based on the reference data and transactions.
    // When the returns of symbols.generate is true, it will trigger pullNewForexRates recursively.
    // Once it returns false, it means that the symbols are up to date.
    const forexRateSubscription = combineLatest([
      baseCurrencyChange$, // base currency changes
      firstTransactionDateChange$, // first transaction date changes
    ]).subscribe(this.onForexRateDataChanged.bind(this));

    this.subscriptions.push(forexRateSubscription);

    const enrichTransactionDataChange$ = combineLatest([
      this.referenceData$.pipe(
        filter((r) => {
          return !!r;
        }),
      ),
      this.transaction$.pipe(
        filter((t) => {
          return !!t;
        }),
      ),
    ]);

    const enrichTransactionSubscription = enrichTransactionDataChange$.subscribe(
      this.onEnrichTransactionDataChanged.bind(this),
    );
    this.subscriptions.push(enrichTransactionSubscription);

    const accountsChange$ = this.accounts$.pipe(
      filter((x) => !!x),
      distinctUntilChanged((previous, current) => {
        return previous === current || (previous.length === 0 && current.length === 0);
      }),
      skipUntil(enrichTransactionDataChange$),
    );

    const accountSubscription = accountsChange$.subscribe(this.onAccountsUpdated.bind(this));
    this.subscriptions.push(accountSubscription);
    this.subscriptions.push(
      combineLatest([this.generatedSymbols.collection$, this.symbols.loadedCollection$]).subscribe(
        ([generatedSymbols]) => {
          this.symbols.merge(generatedSymbols);
        },
      ),
    );
    this.subscriptions.push(
      this.creditReferences$.subscribe((references) => {
        this.referenceData.processNewForexRates(references);
      }),
    );
  }

  private ensureStoreIdleFromInitialise(): void {
    if (this._storeState$.value.status === "Initialise") {
      this._storeState$.next({ status: "Idle" });
    }
  }

  private mergeDirtyFlags(
    base: TransactionStorePage,
    incoming: TransactionStorePage,
  ): TransactionStorePage {
    return {
      isDirtyForexRate: base.isDirtyForexRate || incoming.isDirtyForexRate,
      isDirtySymbols: base.isDirtySymbols || incoming.isDirtySymbols,
      isDirtyTransactions: base.isDirtyTransactions || incoming.isDirtyTransactions,
    };
  }

  /** Entry point: merge dirty flags and run now, or queue one trailing run if busy or sync-blocked. */
  private scheduleRefresh(incoming: TransactionStorePage): void {
    this.ensureStoreIdleFromInitialise();

    if (this.glossSyncService.isRefreshBlocked()) {
      this.log.info("Transaction refresh blocked during sync — merging pending dirty flags");
      this.pendingDirty = this.mergeDirtyFlags(this.pendingDirty ?? NoDirty, incoming);
      return;
    }

    if (this.refreshRunning) {
      this.log.info("Transaction refresh already running — merging pending dirty flags");
      this.pendingDirty = this.mergeDirtyFlags(this.pendingDirty ?? NoDirty, incoming);
      return;
    }
    // DO NOT ADD ANY ASYNC CALL ABOVE THIS LINE
    this.refreshRunning = true;
    void this.runRefreshCycle(incoming);
  }

  private onForexRateDataChanged(): void {
    this.log.info("[TRANSACTION] Change Detected **** Forex rate dependent data changed ****");
    this.scheduleRefresh(StateIsDirty);
  }

  private onEnrichTransactionDataChanged(): void {
    this.log.info("[TRANSACTION] Change Detected **** Reference Data or Transactions changed ****");
    this.scheduleRefresh({ ...StateIsDirty, isDirtyForexRate: false });
  }

  private onAccountsUpdated(_accounts: AccountView[]): void {
    this.log.info("[TRANSACTION] Change Detected **** Accounts added or deleted ****");
    this.scheduleRefresh({ ...StateIsDirty, isDirtyForexRate: false, isDirtySymbols: false });
  }

  private async runRefreshCycle(initialPage: TransactionStorePage): Promise<void> {
    this.log.info("Starting transaction store refresh cycle");
    const epoch = this.storeEpoch;
    const isStale = () => epoch !== this.storeEpoch;

    try {
      let page = initialPage;
      let repeat = true;

      while (repeat) {
        repeat = false;
        page = await this.runForexStage(page);
        if (isStale()) {
          return;
        }

        const symbolsOutcome = await this.runSymbolsStage(page);
        if (isStale()) {
          return;
        }
        if (symbolsOutcome === "fullRerun") {
          this._storeState$.next({ status: "Rerun" });
          page = { ...StateIsDirty };
          repeat = true;
          continue;
        }
        page = symbolsOutcome;

        await this.runTransactionsStage(page, isStale);
      }
    } finally {
      if (isStale()) {
        // clearStore() landed mid-cycle and already reset refreshRunning / pendingDirty; a new
        // session's cycle may own them (and the status) by now, so leave them alone.
        this.log.info("Transaction refresh cycle outlived a store clear — discarding it");
      } else {
        this._storeState$.next({ status: "Idle" });
        this.refreshRunning = false;

        const pending = this.pendingDirty;
        if (pending) {
          this.pendingDirty = null;
          this.scheduleRefresh(pending);
        }
      }
    }
  }

  private async runForexStage(page: TransactionStorePage): Promise<TransactionStorePage> {
    if (!page.isDirtyForexRate) {
      return page;
    }

    this._storeState$.next({ status: "PullingForexRates" });
    this.log.info("Refreshing Forex Rates");
    const coreCurrency = this.userStoreService.preferences.preferenceView().coreCurrency;
    const currencies = this.generatedSymbols.activeSymbols.filter(
      (s) => s !== coreCurrency && isCurrency(s),
    );

    // do not fetch when currencies are empty
    if (currencies.length === 0) {
      return page;
    }

    const firstTransactionDate = this.transaction.firstTransactionDate();

    // no transactions loaded yet - nothing to compute a fetch range from
    if (!firstTransactionDate) {
      return page;
    }

    const range = this.generatedSymbols.recalcDateRange(currencies, [
      firstTransactionDate,
      new Date(),
    ]);

    // null means every currency's requested range is already fully covered by existing history
    if (!range) {
      return page;
    }

    await this.pullNewForexRates(coreCurrency, range.currencies, range.startDate, range.endDate);
    return page;
  }

  private async runSymbolsStage(page: TransactionStorePage): Promise<SymbolsStageResult> {
    if (!page.isDirtySymbols) {
      return page;
    }

    this._storeState$.next({ status: "UpdatingSymbols" });
    this.log.info("Refreshing Symbols");
    const preference = this.userStoreService.preferences.preferenceView();

    const newAdded = this.generatedSymbols.generate(
      preference.coreCurrency,
      this.referenceData.referenceDataViews(),
      this.transaction.transactionViews(),
    );

    if (newAdded) {
      return "fullRerun";
    }
    return page;
  }

  private async runTransactionsStage(
    page: TransactionStorePage,
    isStale: () => boolean,
  ): Promise<void> {
    if (!page.isDirtyTransactions) {
      return;
    }

    this._storeState$.next({ status: "EnrichingTransactions" });
    this.log.info("Refreshing Enrich Transactions");
    const preference = this.userStoreService.preferences.preferenceView();
    const referenceData = this.referenceData.referenceDataViews();
    const transactions = this.transaction.transactionViews();
    const accounts = this.userStoreService.enrichAccounts.collection();

    this.enrichTransaction.generate(preference, referenceData, transactions, accounts);

    this.appState.startProcess("balance-alignment", "Aligning transaction balances...");
    this._storeState$.next({ status: "AligningTransactions" });
    const alignedTransactions = await this.realignBalanceTransactions();
    alignedTransactions.sort(sortTransactionViews);
    this.appState.endProcess("balance-alignment");
    if (isStale()) {
      // Logged out (or otherwise cleared) while the worker was aligning: these are the previous
      // session's transactions, so don't write them back into the cleared collection.
      return;
    }

    this.appState.startProcess("revaluation", "Updating Balances...");
    this.alignedTransactionGeneratedCollection.update(alignedTransactions);
    await this.calculateBalances();
    this.appState.endProcess("revaluation");
  }

  private async pullNewForexRates(
    baseCurrency: string,
    currencies: string[],
    startDate: Date,
    endDate: Date,
  ): Promise<boolean> {
    /** Check what we need to fetch vs has **/
    // todo - Limit the number of currency that need to be fetched.
    // const crossRate = getCrossRateInformation(baseCurrency,currencies, this.referenceData.referenceDataViews()) => check views/reference-data
    // this will fetch the rate for the currencies are used in transactions and reference data aligned with the system preference currency.
    // according to this fact, base in SymbolView is the system preference currency.
    // e.g. system preference currency is HKD, and the user has transactions in EUR and AUDHKD in reference data.
    // check the generate function in symbols.generated.collection.ts for more details.
    // so we fetch HKDEUR and HKDAUD forex rates.
    const epoch = this.storeEpoch;
    try {
      this.log.info(
        `Fetching Forex Rates: ${currencies.join(",")} from ${format(startDate, API_DATE_FORMAT)} to ${format(endDate, API_DATE_FORMAT)}`,
      );
      const forexDataViews = await this.dataPluginStore.forexRates.fetch({
        baseCurrency,
        currencies,
        startDate,
        endDate,
      });
      if (epoch !== this.storeEpoch) {
        // Cleared (e.g. logout) while the fetch was in flight: these are the previous session's
        // currencies, priced in its core currency. Saving them would diff them against whoever's
        // reference data is loaded now and write them into that vault. false is safe for budget's
        // retry gating (ensuredBudgetCurrencies): the budget store is cleared too.
        this.log.info("Forex fetch finished after a store clear — discarding it");
        return false;
      }
      // let's say the query baseCurrency = "USD" and currencies = ["EUR", "HKD"]
      // and the response is USD -> EUR = 0.9 and USD -> HKD = 0.12
      // so we need to flip the rates to EUR -> USD = 1.1 and HKD -> USD = 7.8
      await this.referenceData.processNewForexRates(forexDataViews, (key, forexRateView) => {
        const base = key;
        const symbol = forexRateView.base;
        const rate = 1 / forexRateView.symbols[key];
        return { base, symbol, rate };
      });
      this.log.info("New Forex Rates Imported");
      return true;
    } catch (error) {
      this.log.error("Error fetching forex rates");
      this.log.error(error);
      return false;
    }
  }

  /**
   * Fetch base->currency forex rates for currencies that live OUTSIDE the transaction set
   * (e.g. a budget denominated in a currency you don't otherwise transact in — runForexStage
   * only covers activeSymbols, so those rates would never be pulled). Range spans the full
   * transaction history so per-date conversions resolve; recalcDateRange dedups already-covered
   * currencies, so repeated calls are cheap. Safe to call from async triggers (not render paths).
   */
  async ensureForexRates(currencies: (string | null | undefined)[]): Promise<boolean> {
    // Returns whether the caller can treat these currencies as ensured. A no-op
    // (nothing to fetch / no date range) is `true`; only a genuine forex pull
    // failure returns `false`, so callers gating a retry (e.g. budget's
    // ensuredBudgetCurrencies) don't strand a currency after a transient error.
    const coreCurrency = this.userStoreService.preferences.preferenceView()?.coreCurrency;
    if (!coreCurrency) {
      return true;
    }

    const wanted = [...new Set(currencies)].filter(
      (c): c is string => !!c && c !== coreCurrency && isCurrency(c),
    );
    if (wanted.length === 0) {
      return true;
    }

    const firstTransactionDate = this.transaction.firstTransactionDate();
    if (!firstTransactionDate) {
      return true;
    }

    const range = this.generatedSymbols.recalcDateRange(wanted, [firstTransactionDate, new Date()]);
    if (!range) {
      return true;
    }

    return this.pullNewForexRates(coreCurrency, range.currencies, range.startDate, range.endDate);
  }

  protected getStorageCollection(modelName: StoreModelNames): StoreModelCollection {
    switch (modelName) {
      case "TransactionStoreModel":
        return this.transaction;
      case "ReferenceDataStoreModel":
        return this.referenceData;
      case "SymbolStoreModel":
        return this.symbols;
      default: {
        this.log.warning(`Need to implement TransactionStoreService ${modelName}`);
      }
    }
  }

  async realignBalanceTransactions(): Promise<TransactionView[]> {
    const calculatedTransactions = this.enrichTransaction
      .collection()
      .map((transaction) => transaction.clone());

    if (calculatedTransactions?.length === 0) {
      return calculatedTransactions;
    }

    const parameters: GeneratedTransactionParams = {
      transactions: calculatedTransactions,
      webWorkerQueue: this.webWorkerQueue,
    };
    await this.balanceAlignmentTransactions.generateTransactions(
      parameters,
      this.referenceData.referenceDataViews(),
    );
    return this.balanceAlignmentTransactions.applyBalanceAlignment(
      calculatedTransactions,
      this.referenceData.referenceDataViews(),
    );
  }

  async calculateBalances() {
    this.log.info("Calculating Balances by Accounts and Symbols");
    this._storeState$.next({ status: "CalculatingBalances" });
    const transactions = this.alignedTransactionGeneratedCollection.collection();

    const parameters: CalculatedBalanceParams = {
      webWorkerQueue: this.webWorkerQueue,
      calculatedTransactions: transactions,
    };

    await this.balanceByAccountSymbols.updateCalculations(parameters);
  }

  search(input: string): SearchData {
    const searchTerm = input.trim().toLowerCase();

    return {
      symbol: this.generatedSymbols
        .collection()
        .filter((sym) => sym.code?.trim().toLowerCase().includes(searchTerm)),
      transaction: this.enrichTransaction
        .collection()
        .filter(
          (transaction) =>
            transaction.description?.trim().toLowerCase().includes(searchTerm) &&
            !BALANCE_DEFINITIONS.has(transaction.definition),
        ),
    };
  }

  async purgeAccount(account: AccountView, transactions: TransactionView[]): Promise<boolean> {
    this.enrichTransaction.purgingAccountIds.push(account.id);
    try {
      await this.transaction.deleteFromVault(transactions);
    } catch (error) {
      // deleteFromVault throws (never returns false). A PartialTransactionDeleteError means the
      // surviving partners were repaired but the delete write failed — surface a retry message
      // instead of "not deleted". Either way, always clear the purging id: a bare throw here used
      // to skip this cleanup and leak the id for the rest of the session.
      const messageKey =
        error instanceof PartialTransactionDeleteError
          ? "transactionDeleteIncomplete"
          : `${deletingItem.transaction}NotDeleted`;
      this.confirmationDialogService.notify(NotificationEnum.error, "errorOccurred", messageKey);
      this.enrichTransaction.purgingAccountIds = this.enrichTransaction.purgingAccountIds.filter(
        (id) => id !== account.id,
      );
      return false;
    }

    if (!(await this.userStoreService.accounts.delete(account))) {
      this.confirmationDialogService.notify(
        NotificationEnum.error,
        "errorOccurred",
        `${deletingItem.account}NotDeleted`,
      );
      this.enrichTransaction.purgingAccountIds = this.enrichTransaction.purgingAccountIds.filter(
        (id) => id !== account.id,
      );
      return false;
    }

    this.enrichTransaction.purgingAccountIds = this.enrichTransaction.purgingAccountIds.filter(
      (id) => id !== account.id,
    );
    return true;
  }

  async purgeMultipleAccounts(accounts: AccountView[]): Promise<boolean> {
    let allPurged = true;
    try {
      for (const account of accounts) {
        const accountTransactions = this.getAccountTransaction(account);
        // purgeAccount now catches its own delete failure and returns false (rather than throwing).
        // Continue purging the other accounts, but report overall failure so the consent-revoke
        // callers (which key off isDataDeleted) don't treat a partial purge as fully cleaned.
        if (!(await this.purgeAccount(account, accountTransactions))) {
          allPurged = false;
        }
      }
      return allPurged;
    } catch (error) {
      return false;
    }
  }

  getAccountTransaction(account: AccountView): TransactionView[] {
    return this.enrichTransaction.collection().filter((t) => t.accountLink.id === account.id);
  }

  /**
   * Deletes a vault Symbol along with any reference-data rows keyed by this code as `base`.
   * Those rows are purged first: generatedSymbols (and thus the manage-symbols list) is derived
   * from reference-data history, so leftover rows would regenerate a view and keep the symbol
   * showing up after "deletion".
   *
   * On any failure, purged reference-data rows are restored. Restoring re-creates fresh vault
   * entries rather than updating the original cipher: the purge may already have deleted or
   * shrunk that cipher, so an update-style restore can silently fail against a cipher that no
   * longer exists. Re-creating can leave a harmless duplicate if only part of a multi-cipher
   * purge actually reached the vault before it failed, which is preferable to losing the rows
   * outright.
   *
   * The vault-symbol cipher itself is never re-saved on failure: unlike the multi-row
   * reference-data purge, a single-cipher `symbols.delete()` throw means the delete almost
   * certainly never committed, so the symbol still exists in the vault. Re-creating it there
   * would mint a duplicate cipher sharing the same store-model id.
   */
  async deleteSymbol(symbol: SymbolView): Promise<boolean> {
    try {
      return await this.deleteSymbolInternal(symbol);
    } catch (error) {
      // Backstop for anything outside the two inner try/catches below (e.g. a future change to
      // the synchronous preamble) - never let this reject, or the caller's error toast never shows.
      this.log.error(`Unexpected error deleting symbol ${symbol?.code}`);
      this.log.error(error);
      return false;
    }
  }

  private async deleteSymbolInternal(symbol: SymbolView): Promise<boolean> {
    const vaultView = symbol as unknown as IModelView;
    const storeModel =
      vaultView?.id && typeof vaultView.toStoreModel === "function"
        ? (vaultView.toStoreModel() as any)
        : null;
    if (!storeModel?.vid) {
      this.log.error(`deleteSymbol called with a symbol that has no vault record: ${symbol?.code}`);
      return false;
    }

    const symbolCode = symbol.code;
    const referencesToDelete = (this.referenceData.referenceDataViews() ?? []).filter(
      (rd) => rd.base === symbolCode,
    );

    try {
      if (referencesToDelete.length > 0) {
        await this.referenceData.deleteFromVault(referencesToDelete);
      }
    } catch (error) {
      this.log.error(`Failed to purge reference data for symbol ${symbolCode}`);
      this.log.error(error);
      const referenceDataRestored = await this.restorePurgedReferenceData(
        referencesToDelete,
        symbolCode,
      );
      if (!referenceDataRestored) {
        // Distinguishable from an ordinary delete failure: this is data loss, not a no-op -
        // grep logs for "DATA INTEGRITY" to find cases needing manual vault repair.
        this.log.error(
          `DATA INTEGRITY: reference data for symbol ${symbolCode} may be permanently lost - purge failed and restore also failed`,
        );
      }
      return false;
    }

    try {
      await this.symbols.delete(symbol as any);
      return true;
    } catch (error) {
      this.log.error(`Failed to delete symbol ${symbolCode}`);
      this.log.error(error);
      const referenceDataRestored = await this.restorePurgedReferenceData(
        referencesToDelete,
        symbolCode,
      );
      if (!referenceDataRestored) {
        this.log.error(
          `DATA INTEGRITY: reference data for symbol ${symbolCode} may be permanently lost - vault-symbol delete failed and reference-data restore also failed`,
        );
      }
      return false;
    }
  }

  private async restorePurgedReferenceData(
    referencesToDelete: ReferenceDataView[],
    symbolCode: string,
  ): Promise<boolean> {
    if (referencesToDelete.length === 0) {
      return true;
    }
    try {
      const freshRows = referencesToDelete.map((rd) => toReferenceDataView(rd));
      await this.referenceData.saveToVault(freshRows, true);
      // A dropped create batch now rejects saveToVault (VaultWriter.commitCreate throws
      // PartialVaultWriteError as of #4957), so that path lands in the catch below. This check
      // still guards a narrower case: loadModels() silently skips any saved row that fails
      // validator.assertModel, so a resolved save can still leave a row missing from the reloaded
      // collection. Confirm every row actually landed before reporting a successful restore.
      const restoredIds = new Set(this.referenceData.referenceDataViews()?.map((rd) => rd.id));
      const allRestored = freshRows.every((row) => restoredIds.has(row.id));
      if (!allRestored) {
        this.log.error(
          `Restore of reference data for symbol ${symbolCode} did not fully persist - some rows are missing from the vault`,
        );
      }
      return allRestored;
    } catch (restoreError) {
      this.log.error(`Failed to restore reference data for symbol ${symbolCode}`);
      this.log.error(restoreError);
      return false;
    }
  }
}
